import React, { useEffect, useState } from "react";
import { Banknote, Clock, ShieldAlert, AlertTriangle, FileWarning, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const hrs = (n) => `${Number(n || 0).toFixed(1)}h`;

// Fatigue status → colour. "approaching"/"over" mirror the admin thresholds.
const FATIGUE_STYLE = {
  ok: { color: "#166534", label: "Within limits" },
  approaching: { color: "#92400E", label: "Approaching limit" },
  over: { color: "#B91C1C", label: "Over limit" },
};

const STATUS_STYLE = {
  expired: { color: "#B91C1C", bg: "#FEF2F2" },
  expiring: { color: "#92400E", bg: "#FFFBEB" },
  unset: { color: "#6B7280", bg: "#F9FAFB" },
  valid: { color: "#166534", bg: "#F0FDF4" },
};

const Tile = ({ children, testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>{children}</div>
);

/**
 * DriverDashboardPanel — operational / compliance / business info that stays on
 * the Driver Dashboard (NOT the navigation screen, by design). Read-only: pulls
 * existing /driver/kpi, /driver/fatigue and /driver/compliance. Never mutates
 * earnings, fatigue or dispatch state.
 */
export const DriverDashboardPanel = () => {
  const [kpi, setKpi] = useState(null);
  const [fatigue, setFatigue] = useState(null);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [k, f, c] = await Promise.allSettled([
          api.get("/driver/kpi"),
          api.get("/driver/fatigue"),
          api.get("/driver/compliance"),
        ]);
        if (!alive) return;
        if (k.status === "fulfilled") setKpi(k.value.data);
        if (f.status === "fulfilled") setFatigue(f.value.data);
        if (c.status === "fulfilled") setCompliance(c.value.data);
      } catch { /* read-only widget — silent on failure */ }
    })();
    return () => { alive = false; };
  }, []);

  const today = kpi?.today;
  const week = kpi?.week;
  const fh = fatigue?.hours;
  const fref = fatigue?.reference;
  const fStatus = FATIGUE_STYLE[fh?.status] || FATIGUE_STYLE.ok;
  const alerts = (compliance?.items || []).filter((i) => i.status === "expired" || i.status === "expiring");

  return (
    <div className="space-y-3" data-testid="driver-dashboard-panel">
      <div className="label-mini text-muted-foreground">DRIVER DASHBOARD</div>

      <div className="grid grid-cols-2 gap-3">
        {/* Today's Earnings */}
        <Tile testid="dash-earnings">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <Banknote className="w-3.5 h-3.5" /> Today
          </div>
          <div className="font-heading font-black text-2xl mt-1" data-testid="dash-earnings-net">
            {today ? gbp(today.net) : "—"}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {today ? `${gbp(today.gross)} gross · ${today.jobs} jobs` : "Loading…"}
          </div>
        </Tile>

        {/* Driving Hours + Fatigue */}
        <Tile testid="dash-hours">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <Clock className="w-3.5 h-3.5" /> Driving hours
          </div>
          <div className="font-heading font-black text-2xl mt-1" data-testid="dash-hours-today">
            {fh ? hrs(fh.driving_hours_today) : "—"}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {fh ? `${hrs(fh.driving_hours_week)} this week` : "Loading…"}
          </div>
          {fh && (
            <div className="flex items-center gap-1 mt-2 text-[11px] font-bold" style={{ color: fStatus.color }} data-testid="dash-fatigue-status">
              {fh.status === "ok"
                ? <CheckCircle2 className="w-3.5 h-3.5" />
                : <AlertTriangle className="w-3.5 h-3.5" />}
              {fStatus.label}
              {fref && fh.status !== "ok" ? ` (limit ${fref.driving_day_warn}h/day)` : ""}
            </div>
          )}
        </Tile>
      </div>

      {/* Compliance / Document Expiry alerts */}
      <Tile testid="dash-compliance">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <ShieldAlert className="w-3.5 h-3.5" /> Compliance &amp; documents
          </div>
          {compliance && (
            <span className="text-[11px] font-bold" style={{ color: alerts.length ? "#B91C1C" : "#166534" }} data-testid="dash-compliance-count">
              {alerts.length ? `${alerts.length} alert${alerts.length > 1 ? "s" : ""}` : "All valid"}
            </span>
          )}
        </div>

        {alerts.length === 0 ? (
          <div className="flex items-center gap-1.5 text-xs text-[#166534] mt-2">
            <CheckCircle2 className="w-4 h-4" /> No documents expiring soon.
          </div>
        ) : (
          <div className="space-y-1.5 mt-2">
            {alerts.map((i) => {
              const s = STATUS_STYLE[i.status] || STATUS_STYLE.unset;
              const msg = i.status === "expired"
                ? `${i.doc_label} expired${i.days_remaining != null ? ` ${Math.abs(i.days_remaining)} days ago` : ""}`
                : `${i.doc_label} expires in ${i.days_remaining} day${i.days_remaining === 1 ? "" : "s"}`;
              return (
                <div
                  key={i.doc_type}
                  className="flex items-center gap-2 px-2.5 py-1.5 text-xs font-semibold"
                  style={{ color: s.color, background: s.bg }}
                  data-testid={`dash-compliance-${i.doc_type}`}
                >
                  <FileWarning className="w-4 h-4 shrink-0" /> {msg}
                </div>
              );
            })}
          </div>
        )}
      </Tile>
    </div>
  );
};

export default DriverDashboardPanel;
